import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://topnationapp-1-api.onrender.com";

export default function AvatarUpload({ onUploadSuccess }) {
  const [avatar, setAvatar] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCurrentAvatar();
  }, []);

  const loadCurrentAvatar = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.profile_image) {
          setAvatar(data.profile_image);
        }
      }
    } catch (err) {
      console.error("Failed to load profile:", err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(false);

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setError("Only JPG and PNG files allowed");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError("File must be under 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => setPreview(event.target.result);
    reader.readAsDataURL(file);

    uploadAvatar(file);
  };

  const uploadAvatar = async (file) => {
    const token = localStorage.getItem("token");

    if (!token) {
      setError("Please login first");
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${API_URL}/api/avatar/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = res.data;

      setAvatar(data.profile_image || null);
      setPreview(null);
      setSuccess(true);

      if (onUploadSuccess) {
        onUploadSuccess(data.profile_image || null);
      }

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Avatar upload error:", err);

      // 🔥 FIX – vždy STRING, nikdy OBJECT
      const detail = err?.response?.data?.detail;

      let message = "Upload failed";

      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map((x) => x?.msg || "").join(", ");
      } else if (err?.message) {
        message = err.message;
      }

      setError(String(message));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const displayImage = preview || avatar;

  return (
    <div className="flex flex-col items-center gap-4 p-6 bg-[#0a0a0a] rounded-xl border border-[#222]">
      <h3 className="text-lg font-semibold text-[#FFD700]">
        Profile Avatar
      </h3>

      <div className="relative">
        <div
          className="w-28 h-28 rounded-full overflow-hidden bg-[#1a1a1a] flex items-center justify-center"
          style={{
            border: "3px solid #FFD700",
            boxShadow: "0 0 20px rgba(255, 215, 0, 0.3)",
          }}
        >
          {displayImage ? (
            <img
              src={displayImage}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          ) : (
            <svg
              className="w-14 h-14 text-gray-600"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
        </div>

        {uploading && (
          <div className="absolute inset-0 bg-black/70 rounded-full flex items-center justify-center">
            <div
              className="w-10 h-10 rounded-full animate-spin"
              style={{
                border: "3px solid #FFD700",
                borderTopColor: "transparent",
              }}
            />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleFileSelect}
        className="hidden"
      />

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="px-6 py-2.5 rounded-lg font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: "linear-gradient(135deg, #FFD700, #FFA500)",
          boxShadow: "0 0 15px rgba(255, 215, 0, 0.4)",
        }}
      >
        {uploading ? "Uploading..." : avatar ? "Change Avatar" : "Upload Avatar"}
      </motion.button>

      <p className="text-xs text-gray-500">
        JPG or PNG • Max 2MB
      </p>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-red-500 text-sm text-center break-words max-w-[240px]"
        >
          {error}
        </motion.p>
      )}

      {success && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-green-500 text-sm text-center"
        >
          ✓ Avatar saved! It will appear in your next bid.
        </motion.p>
      )}
    </div>
  );
}
